import React, { useState, useEffect, useCallback } from "react";
import {
  migrateData,
  isMigrationComplete,
  setUseApiFlag,
} from "../../services/database";
import { keyframes } from "@emotion/react";

interface DataMigrationProps {
  onMigrationComplete: () => void;
}

const DataMigration: React.FC<DataMigrationProps> = ({
  onMigrationComplete,
}) => {
  const [migrationStatus, setMigrationStatus] = useState<
    "pending" | "in_progress" | "success" | "error"
  >("pending");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleMigration = useCallback(async () => {
    setMigrationStatus("in_progress");

    try {
      const success = await migrateData();

      if (success) {
        setMigrationStatus("success");
        setUseApiFlag(true);

        // Give a small delay for the success message to be shown
        setTimeout(() => {
          onMigrationComplete();
        }, 1500);
      } else {
        setMigrationStatus("error");
        setErrorMessage(
          "Migration failed. Please try again or continue without migration."
        );
      }
    } catch (error) {
      setMigrationStatus("error");
      setErrorMessage(
        `Migration error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }, [onMigrationComplete]);

  useEffect(() => {
    // Check if migration is already complete
    if (isMigrationComplete()) {
      onMigrationComplete();
      return;
    }

    // Otherwise, start the migration
    handleMigration();
  }, [onMigrationComplete, handleMigration]);

  const skipMigration = () => {
    setUseApiFlag(false);
    onMigrationComplete();
  };

  const retry = () => {
    handleMigration();
  };

  // Styles
  const containerStyle = {
    position: "fixed" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  };

  const modalStyle = {
    backgroundColor: "white",
    padding: "24px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    maxWidth: "450px",
    width: "90%",
  };

  const titleStyle = {
    fontSize: "20px",
    fontWeight: "bold" as const,
    marginBottom: "16px",
  };

  const messageStyle = {
    marginBottom: "24px",
    lineHeight: "1.5",
  };

  const buttonStyle = {
    padding: "8px 16px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    marginRight: "8px",
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#3182CE",
    color: "white",
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: "#EDF2F7",
    color: "#2D3748",
  };

  const spin = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;

  const loaderStyle = {
    display: "inline-block",
    width: "24px",
    height: "24px",
    border: "3px solid rgba(0, 0, 0, 0.1)",
    borderRadius: "50%",
    borderTopColor: "#3182CE",
    animation: `${spin} 1s ease-in-out infinite`,
    marginRight: "8px",
  };

  return (
    <div style={containerStyle}>
      <div style={modalStyle}>
        <h2 style={titleStyle}>Data Migration</h2>

        {migrationStatus === "pending" && (
          <>
            <p style={messageStyle}>
              We need to migrate your finance data to our new database system.
              This will ensure your data is accessible across different sessions
              and ports. Would you like to proceed?
            </p>
            <div>
              <button style={primaryButtonStyle} onClick={handleMigration}>
                Migrate Data
              </button>
              <button style={secondaryButtonStyle} onClick={skipMigration}>
                Skip (Not Recommended)
              </button>
            </div>
          </>
        )}

        {migrationStatus === "in_progress" && (
          <>
            <p style={messageStyle}>
              <div style={loaderStyle as any} /> Migrating your data... Please
              wait.
            </p>
          </>
        )}

        {migrationStatus === "success" && (
          <>
            <p style={messageStyle}>
              Data migration completed successfully! Redirecting to the
              application...
            </p>
          </>
        )}

        {migrationStatus === "error" && (
          <>
            <p style={messageStyle}>{errorMessage}</p>
            <div>
              <button style={primaryButtonStyle} onClick={retry}>
                Retry Migration
              </button>
              <button style={secondaryButtonStyle} onClick={skipMigration}>
                Continue without Migration
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DataMigration;
